import { set } from 'lodash';
import moment from 'moment';
import { EventTypes } from 'redux-segment';

import { LT_FETCH } from 'bl/loggers.bl';
import FLAG from 'config/flag.singleton';
import SENTRY from 'config/sentry.singleton';
import CT from 'constants/contentType.const';
import HDR from 'constants/httpHeader.const';
import MIME from 'constants/mime.const';
import { AUTH_API } from 'middleware/api';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import callMock from 'utils/backend/callMock.util';
import createCallException from 'utils/backend/createCallException.util';
import determineConfig from 'utils/backend/determineConfig.util';
import determineHeaders from 'utils/backend/determineHeaders.util';
import fetcher from 'utils/backend/fetcher.util';
import handleErrorThrow from 'utils/backend/handleErrorThrow.util';
import inspectError from 'utils/backend/inspectError.util';
import inspectResponse from 'utils/backend/inspectResponse.util';
import translateErrorOld from 'utils/backend/translateErrorOld.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { nowAsZulu } from 'utils/date/now.util';
import { isEnvCy, isEnvTest } from 'utils/env/determine.util';
import asFunction from 'utils/functions/asFunction.util';
import sleep from 'utils/functions/sleep.util';
import withCachedPromise from 'utils/functions/withCachedPromise.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFn from 'utils/predicates/function/isFn.util';
import clampOrMax from 'utils/values/clampOrMax.util';

import { handleNotification } from 'notification/actions';

const L = logger(['beVersion1', LT_FETCH]);

const MTAG = 'beV1';

// FIXME: aborting unsafe calls messes up older tests, those should be made narrower or have *them* set these flags
const USE_ABORT_PIN_UNSAFE_CALLS = !isEnvTest();
const USE_LOG_PIN_UNSAFE_CALLS = true;

const REGEX_ALL_SALON_ID = /:salonId/g;

const IS_CYPRESS = isEnvCy();
const RETRY_DELAY = IS_CYPRESS ? 20 : 200;
const MAX_RETRIES = IS_CYPRESS ? 2 : 6;

const LOGGER = 'callerV1';
const V = 1;

const callBe = withCachedPromise(
  async ({
    apiRoot,
    endpoint,
    authenticated,
    maxRetries: maxRetriesInput,
    retryDelay: retryDelayInput,
    config,
    salonId,
    store,
    handleNotification,
  }) => {
    const maxRetries = clampOrMax(0, MAX_RETRIES, maxRetriesInput);
    const retryDelay = clampOrMax(0, RETRY_DELAY, retryDelayInput);

    try {
      // eslint-disable-next-line no-param-reassign
      config.headers = determineHeaders({ authenticated, store, config });
    } catch (e) {
      if (!IS_CYPRESS) {
        store.dispatch(
          handleNotification(translateErrorOld(e.message || 'Unknown error', 'api:callApi'), 'danger', true, false),
        );
      }
    }

    // for the case of 403, there might be multiple retries until a success or limit reached
    let retries = 0;
    let response = await fetcher({ ...config, v: V, url: `${apiRoot}${endpoint}` });

    // FIXME: this check shouldn't exist if fetcher v1 is fixed to not return undefined
    if (!response) return void 1;

    while (response && response.status === 403 && retries < maxRetries) {
      retries += 1;

      // eslint-disable-next-line no-await-in-loop,no-mixed-operators
      await sleep(retryDelay * 2 ** retries);

      // Cognito token may have updated while we were waiting, re-determine the authentication
      // eslint-disable-next-line no-param-reassign
      config.headers = determineHeaders({ authenticated, store, config });

      // eslint-disable-next-line no-await-in-loop
      response = await fetcher({ ...config, v: V, url: `${apiRoot}${endpoint}` });
    }

    // Exponential backoff and retry with limit
    let json = {};

    // 204 == No Content, so don't try to parse
    if (response && response.status !== 204) {
      if (response.headers.get('Content-Type') === MIME.json || IS_CYPRESS) {
        json = await response.json();
      } else {
        if (response.ok) {
          return { response, json: undefined, type: CT.text, ctype: CT.text, text: await response.text() };
        }

        SENTRY.becall({
          logger: LOGGER,
          v: V,
          salonId,
          status: asObject(response).status,
          method: asObject(config).method,
          base: apiRoot,
          path: endpoint,
          json,
          extra: { checkpoint: 1 },
        });

        throw createCallException({
          message: `Unknown error with code ${response.status}`,
          code: response.status,
          body: response.body,
        });
      }
    }

    if (response?.ok) return { response, json, type: CT.json, ctype: CT.json };

    // if there was a problem, dispatch the error condition
    SENTRY.becall({
      logger: LOGGER,
      v: V,
      salonId,
      status: asObject(response).status,
      method: asObject(config).method,
      base: apiRoot,
      path: endpoint,
      json,
      extra: { checkpoint: 2 },
    });

    // when authentication required, code 402
    if (response?.status === 402) {
      return { response, json: { error: 'authentication_required' }, type: CT.json, ctype: CT.text };
    }

    // this function is for side effects i.e. throw an error
    return void handleErrorThrow({ json, response, ctype: CT.text });
  },
);

const beVersion1 = async (options) => {
  const next = asFunction(options?.next);

  const ATAG = fromAnyToTrimmed(options?.action?.ATAG);
  const action = options?.action ?? {};
  const store = options?.store ?? {};
  const api = action?.[AUTH_API] ?? {};

  const {
    api_root: apiRoot,
    authenticated,
    endpoint: originalEndpoint,
    maxRetries,
    params,
    pinSafe,
    retryDelay,
    types: [initType, passType, failType, doneType],
    mock,
    notifyOnError,
  } = api;

  // FIXME: some values here (like headers) are mutated

  try {
    const state = store.getState();
    const config = determineConfig({ api, state });
    const salonId = selectCurrentSalonId(state);

    // simplistic way of replacing missing salonId with most likely valid value
    // SEE: src/utils/backend/endpoint.util.js

    const endpoint = originalEndpoint.replace(REGEX_ALL_SALON_ID, salonId);

    if (USE_ABORT_PIN_UNSAFE_CALLS && !pinSafe && !config.headers[HDR.lt]) {
      const errorMessage = `Aborted pin-unsafe call for ${config.method} ${endpoint}`;
      if (USE_LOG_PIN_UNSAFE_CALLS) L.debug('()', errorMessage);

      const meta = FLAG.trackBeCalls ? { analytics: EventTypes.track } : void 1;
      return void next({ ATAG, MTAG, errorMessage, params, isFetching: false, type: failType, meta });
    }

    if (FLAG.trackBeCalls && action?.meta?.analytics) {
      next(
        set(
          { ATAG, MTAG, type: 'EVENT_TRACKING', ...action },
          `${AUTH_API}.config.headers`,
          determineHeaders({ authenticated, store, config }),
        ),
      );
    }

    next({ ATAG, MTAG, type: initType, isFetching: true, params });

    const meta = { analytics: EventTypes.track };
    const fieldsBefore = { ATAG, MTAG, type: passType, authenticated, params, isFetching: false, meta };

    const call = isFn(mock) ? callMock(mock) : callBe;
    const result = await call({
      apiRoot,
      authenticated,
      config,
      endpoint,
      maxRetries,
      requestType: initType,
      retryDelay,
      salonId,
      store,
      handleNotification,
    });

    const receivedAt = nowAsZulu();
    const { type, text, json, response: actualResponse } = result ?? {};
    const { status } = actualResponse ?? {};
    const is = inspectResponse(actualResponse);
    const response = type === 'text' ? text : json;
    const responseReceived = moment(receivedAt);

    const fieldsAfter = { is, response, responseReceived, receivedAt, result, actualResponse, status, text, json };

    const input = { ...fieldsBefore, ...fieldsAfter };
    const jsonInput = FLAG.loggerBeVersion1 ? JSON.stringify(input) : void 1;

    const output = next(input);

    if (FLAG.loggerBeVersion1) {
      const jsonOutput = JSON.stringify(output);
      L.debug('()', ATAG, MTAG, jsonOutput === jsonInput, jsonOutput, '<-', jsonInput, '<-', options);
    }

    return output;
  } catch (error) {
    L.warn('()', 'failType:', failType, 'error:', error);

    const receivedAt = nowAsZulu();
    const { status } = asObject(error);
    const is = inspectError(error);
    const errorMessage = error.message || 'Unknown Call error';
    const meta = FLAG.trackBeCalls ? { analytics: EventTypes.track } : void 1;

    const fieldsBefore = { ATAG, MTAG, type: failType, authenticated, params, isFetching: false, meta };
    const fieldsAfter = { errorMessage, error, is, receivedAt, status };

    if (!failType) {
      store.dispatch(handleNotification(translateErrorOld(errorMessage, 'api:dispatch'), 'danger', true, false));
      return next({ ATAG, MTAG, type: '' });
    }

    if (notifyOnError) {
      store.dispatch(handleNotification(translateErrorOld(errorMessage, 'api:dispatch'), 'danger', true, false));
    }

    return next({ ...fieldsBefore, ...fieldsAfter });
  } finally {
    if (doneType) next({ ATAG, MTAG, authenticated, isFetching: false, type: doneType, params });
  }
};

beVersion1.MTAG = MTAG;

// noinspection JSUnusedGlobalSymbols
export default beVersion1;
